import { ArrowUpRight, Facebook, Instagram, Linkedin } from 'lucide-react';
import { BRAND, DISCLAIMER } from '@/data/site';
import { DISCLAIMER_EN } from '@/data/en/site';
import { useLang, LangLink } from '@/i18n/LangContext';

const XIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.657l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
  </svg>
);

const SOCIAL_LINKS = [
  { label: 'LinkedIn', href: 'https://www.linkedin.com/in/matteo-vairani/', icon: Linkedin },
  { label: 'X (Twitter)', href: 'https://x.com/unity_swiss', icon: XIcon },
  { label: 'Instagram', href: 'https://www.instagram.com/unity_swiss/', icon: Instagram },
  { label: 'Facebook', href: 'https://www.facebook.com/people/Unity-Consulting/61593061991487/#', icon: Facebook },
];

const COLUMNS_EN = [
  {
    title: 'Solutions',
    links: [
      { label: 'Health', href: '/cassa-malati' },
      { label: 'Premium calculator 2026–2027', href: '/cassa-malati/calcolatore-premi' },
      { label: 'Individuals', href: '/soluzioni/privati' },
      { label: 'Businesses', href: '/soluzioni/aziende' },
      { label: 'Pension', href: '/soluzioni/previdenza' },
      { label: 'Claims centre', href: '/centro-sinistri' },
    ],
  },
  {
    title: 'Guides',
    links: [
      { label: 'Moving to Switzerland', href: '/guide/privati/trasferirsi-in-svizzera' },
      { label: 'Switching health insurers', href: '/guide/salute/cambio-cassa-malati-premi-disdetta' },
      { label: 'Buying a home at 30', href: '/guide/privati/comprare-casa-a-30-anni' },
      { label: 'Going self-employed', href: '/guide/privati/mettersi-in-proprio' },
      { label: 'Starting a business', href: '/guide/aziende/aprire-un-azienda' },
    ],
  },
  {
    title: 'Agency',
    links: [
      { label: 'About us', href: '/chi-siamo' },
      { label: 'Mission', href: '/chi-siamo/missione' },
      { label: 'Contact', href: '/contatti' },
      { label: 'Refer someone', href: '/consiglio-a-terzi' },
      { label: 'Referrer agreement', href: '/accordo-collaborazione-indicatori' },
      { label: 'Privacy policy', href: '/privacy' },
      { label: 'Cookie policy', href: '/cookie-policy' },
      { label: 'Legal notice', href: '/note-legali' },
    ],
  },
];

const COLUMNS = [
  {
    title: 'Soluzioni',
    links: [
      { label: 'Salute', href: '/cassa-malati' },
      { label: 'Calcolatore premi 2026–2027', href: '/cassa-malati/calcolatore-premi' },
      { label: 'Privati', href: '/soluzioni/privati' },
      { label: 'Aziende', href: '/soluzioni/aziende' },
      { label: 'Previdenza', href: '/soluzioni/previdenza' },
      { label: 'Centro sinistri', href: '/centro-sinistri' },
    ],
  },
  {
    title: 'Guide',
    links: [
      { label: 'Trasferirsi in Svizzera', href: '/guide/privati/trasferirsi-in-svizzera' },
      { label: 'Cambio cassa malati', href: '/guide/salute/cambio-cassa-malati-premi-disdetta' },
      { label: 'Comprare casa a 30 anni', href: '/guide/privati/comprare-casa-a-30-anni' },
      { label: 'Mettersi in proprio', href: '/guide/privati/mettersi-in-proprio' },
      { label: 'Aprire un’azienda', href: '/guide/aziende/aprire-un-azienda' },
    ],
  },
  {
    title: 'Agenzia',
    links: [
      { label: 'Chi siamo', href: '/chi-siamo' },
      { label: 'Missione', href: '/chi-siamo/missione' },
      { label: 'Contatti', href: '/contatti' },
      { label: 'Consiglio a terzi', href: '/consiglio-a-terzi' },
      { label: 'Accordo indicatori', href: '/accordo-collaborazione-indicatori' },
      { label: 'Privacy policy', href: '/privacy' },
      { label: 'Cookie policy', href: '/cookie-policy' },
      { label: 'Note legali', href: '/note-legali' },
    ],
  },
];

export const Footer = () => {
  const { lang, t } = useLang();
  const columns = lang === 'en' ? COLUMNS_EN : COLUMNS;
  const disclaimer = lang === 'en' ? DISCLAIMER_EN : DISCLAIMER;

  return (
    <footer data-testid="site-footer" className="bg-brand-deep text-white">
      <div className="mx-auto max-w-site px-5 md:px-8 py-16 md:py-24">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-3">
            <div className="flex items-center gap-3 mb-6">
              <img src={BRAND.logoMark} alt="" className="h-10 w-10 brightness-0 invert" />
              <span className="font-heading text-xl font-bold tracking-tight">Unity Consulting</span>
            </div>
            <p className="text-white/70 text-sm leading-relaxed max-w-xs mb-8">
              {t('footerTagline')}
            </p>
            <div className="mb-6 flex items-center gap-2" aria-label="Social media">
              {SOCIAL_LINKS.map(({ label, href, icon: Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  title={label}
                  className="grid h-10 w-10 place-items-center rounded-full border border-white/20 text-white/70 transition-colors hover:border-brand-light hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-light"
                >
                  <Icon className="h-[18px] w-[18px]" />
                </a>
              ))}
            </div>
            <LangLink
              to="/centro-sinistri"
              data-testid="footer-claims-link"
              className="inline-flex items-center gap-2 border border-white/25 px-4 py-2.5 text-sm font-medium rounded-lg hover:bg-white/10 transition-colors duration-200"
            >
              {t('footerClaims')}
              <ArrowUpRight className="h-4 w-4" />
            </LangLink>
          </div>

          {columns.map((col) => (
            <div key={col.title} className="lg:col-span-3 lg:justify-self-end lg:min-w-[10rem] text-left">
              <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-light mb-5">{col.title}</h4>
              <ul className="space-y-3 text-left">
                {col.links.map((link) => (
                  <li key={link.href + link.label}>
                    <LangLink to={link.href} className="text-sm text-white/70 hover:text-white transition-colors duration-150">
                      {link.label}
                    </LangLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-16 pt-8 border-t border-white/10">
          <p className="text-xs text-white/50 leading-relaxed max-w-3xl mb-4">{disclaimer}</p>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <p className="text-xs text-white/40">© {new Date().getFullYear()} Unity Consulting — {t('footerRights')}</p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 sm:justify-end">
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent('unity:open-cookie-settings'))}
                className="w-fit text-xs text-white/60 underline underline-offset-4 transition-colors hover:text-white"
              >
                {t('footerCookiePrefs')}
              </button>
              <a
                href="https://www.niu.swiss"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-white/45 transition-colors hover:text-white"
              >
                powered by www.niu.swiss
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
